import type { CityDto, MeDto, TaskDto } from '@setu/shared';

/** A failure the server meant to send, with the field errors forms need. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}


/**
 * The server sends per-field validation messages alongside its generic
 * "Some of that needs another look." — show the specifics, or the person
 * cannot tell which field to fix.
 */
export function apiErrorText(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError) {
    if (err.fields) {
      const parts = Object.entries(err.fields).map(([field, message]) => `${prettyField(field)}: ${message}`);
      if (parts.length > 0) return parts.join(' ');
    }
    return err.message;
  }
  return fallback;
}

function prettyField(key: string): string {
  const names: Record<string, string> = {
    name: 'Name',
    email: 'Email',
    password: 'Password',
    cityId: 'City',
    cityName: 'City',
    rcArea: 'RC area',
    rcAreaName: 'RC area',
    otherSkills: 'Other skill',
    phone: 'Phone',
  };
  return names[key] ?? key;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      // The session is an httpOnly cookie; nothing here touches a token.
      credentials: 'same-origin',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(0, 'offline', "Can't reach Setu. Check your connection and try again.");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? safeJson(text) : {};

  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string; fields?: Record<string, string> } }).error;
    throw new ApiError(
      res.status,
      err?.code ?? 'unknown',
      err?.message ?? 'Something went wrong.',
      err?.fields,
    );
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

/**
 * Uploads go up as raw bytes with the file's own mime, not JSON — so this
 * bypasses `request` while keeping the same error shape and cookie handling.
 */
export async function uploadFile(file: File): Promise<{ file: { id: string; name: string } }> {
  let res: Response;
  try {
    res = await fetch('/api/files', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': file.type || 'application/octet-stream',
        'x-file-name': encodeURIComponent(file.name),
      },
      body: file,
    });
  } catch {
    throw new ApiError(0, 'offline', "Can't reach Setu. Check your connection and try again.");
  }

  const text = await res.text();
  const body = text ? safeJson(text) : {};
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string; fields?: Record<string, string> } }).error;
    throw new ApiError(res.status, err?.code ?? 'unknown', err?.message ?? 'That upload did not go through.', err?.fields);
  }
  return body as { file: { id: string; name: string } };
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export interface InvitePreview {
  email: string;
  name: string | null;
  role: string;
  cityName: string | null;
  areaName: string | null;
  skills: string[];
}

export const api = {
  me: () => get<{ user: MeDto | null }>('/api/auth/me'),
  login: (email: string, password: string) => post<{ user: MeDto }>('/api/auth/login', { email, password }),
  unsubscribe: (token: string, kind: string, resubscribe: boolean) =>
    post<{ ok: true }>('/api/unsubscribe', { token, kind, resubscribe }),
  forgotPassword: (email: string) => post<{ ok: true }>('/api/auth/forgot', { email }),
  checkReset: (token: string) => post<{ valid: boolean }>('/api/auth/reset/check', { token }),
  resetPassword: (token: string, newPassword: string) =>
    post<{ user: MeDto }>('/api/auth/reset', { token, newPassword }),
  logout: () => post<{ ok: true }>('/api/auth/logout'),

  // POST, not GET with the token in the path: the token is a credential and
  // a URL path lands in access logs, browser history and every proxy between.
  invitePreview: (token: string) => post<{ invite: InvitePreview }>('/api/auth/invite/preview', { token }),
  acceptInvite: (body: {
    token: string;
    name: string;
    password: string;
    skills: string[];
    otherSkills: string;
    learn: string[];
    weeklyMinutes: number;
  }) => post<{ user: MeDto }>('/api/auth/invite/accept', body),

  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ ok: true }>('/api/auth/password', { currentPassword, newPassword }),

  /** Open work matched to me. */
  openTasks: (area?: string) => get<{ tasks: TaskDto[] }>(`/api/tasks${area ? `?area=${area}` : ''}`),
  /** What I have said yes to, and what I have finished. */
  myTasks: () => get<{ tasks: TaskDto[] }>('/api/tasks?mine=true'),
  task: (id: string) => get<{ task: TaskDto }>(`/api/tasks/${id}`),
  actOnTask: (id: string, action: string, extra: { note?: string; proof?: string; proofFileId?: string } = {}) =>
    post<{ task: TaskDto }>(`/api/tasks/${id}/action`, { action, ...extra }),

  cities: () => get<{ cities: CityDto[] }>('/api/cities'),
  updateProfile: (body: Record<string, unknown>) => patch<{ user: MeDto }>('/api/me', body),
};

/* ------------------------------------------------------- phase 2 surface */

export interface CampaignDto {
  id: string;
  slug: string;
  name: string;
  blurb: string;
  detail: string;
  startsOn: string | null;
  endsOn: string | null;
  status: string;
  goal: { unit: string; target: number; window: string } | null;
  log: { cta: string; prompt: string } | null;
  cityLogged: number;
  myLogged: number;
  interested: number;
  joined: boolean;
}

export interface RhythmDto {
  id: string;
  title: string;
  blurb: string;
  cadence: string;
  proofPrompt: string | null;
  campaignName: string | null;
  mine: boolean;
  streak: number;
  best: number;
  doneToday: boolean;
  totalDone: number;
  /** Where to actually go do the thing, when the rhythm has one. */
  linkUrl: string | null;
}

export interface ThreadSummary {
  id: string;
  kind: string;
  title: string;
  subtitle: string;
  lastMessage: string | null;
  lastAt: string | null;
  unread: number;
  memberCount: number;
}

export interface MessageDto {
  id: string;
  body: string;
  createdAt: string;
  senderId: string;
  senderName: string;
  mine: boolean;
  attachment: { id: string; name: string; mime: string } | null;
}

export interface NoteDto {
  id: string;
  body: string;
  fromName: string;
  fromRole: string;
  createdAt: string;
  readAt: string | null;
}

export interface IdeaDto {
  id: string;
  title: string;
  pitch: string;
  /** 'event_proposal' brings the structured fields below; plain ideas do not. */
  kind: 'idea' | 'event_proposal';
  eventDate: string | null;
  eventVenue: string | null;
  eventCost: string | null;
  attachment: { id: string; name: string } | null;
  area: string | null;
  status: string;
  cityName: string | null;
  citiesAdopted: number;
  runningHere: boolean;
  mine: boolean;
  createdAt: string;
  notes: { kind: string; body: string; cityName: string | null }[];
}

export interface LibraryItem {
  id: string;
  category: string;
  title: string;
  format: string;
  blurb: string;
  body: string[];
  /** Name of the coordinator who shared this kit with you; null when it arrived by normal scope. */
  sharedBy: string | null;
}

export interface QuestionDto {
  id: string;
  body: string;
  answer: string | null;
  answered_at: string | null;
  created_at: string;
  answered_by: string | null;
}

export interface CheckinDto {
  weekOf: string;
  did: string;
  wentWell: string;
  wentBadly: string;
  wouldHelp: string;
}

export const api2 = {
  campaigns: () => get<{ campaigns: CampaignDto[] }>('/api/campaigns'),
  campaignInterest: (id: string, joining: boolean) =>
    post<{ campaigns: CampaignDto[] }>(`/api/campaigns/${id}/interest`, { joining }),
  campaignLog: (id: string, body: { link?: string; note?: string }) =>
    post<{ campaigns: CampaignDto[] }>(`/api/campaigns/${id}/log`, body),

  rhythms: () => get<{ rhythms: RhythmDto[] }>('/api/rhythms'),
  rhythmCommit: (id: string, taking: boolean) =>
    post<{ rhythms: RhythmDto[] }>(`/api/rhythms/${id}/commit`, { taking }),
  rhythmDone: (id: string, body: { proof?: string; note?: string; fileId?: string }) =>
    post<{ rhythms: RhythmDto[] }>(`/api/rhythms/${id}/done`, body),

  threads: () => get<{ threads: ThreadSummary[] }>('/api/threads'),
  thread: (id: string) => get<{ messages: MessageDto[] }>(`/api/threads/${id}`),
  send: (id: string, body: string, fileId?: string) =>
    post<{ message: MessageDto }>(`/api/threads/${id}/messages`, { body, fileId }),
  openDirect: (userId: string) => post<{ threadId: string }>('/api/threads/direct', { userId }),
  myCoordinators: () =>
    get<{ coordinators: { id: string; name: string; role: string; city_name: string | null }[] }>(
      '/api/my-coordinators',
    ),

  notes: () => get<{ notes: NoteDto[] }>('/api/notes'),
  markNoteRead: (id: string) => post<{ ok: true }>(`/api/notes/${id}/read`),

  ideas: () => get<{ mine: IdeaDto[]; shared: IdeaDto[] }>('/api/ideas'),
  pitchIdea: (body: {
    title: string;
    pitch: string;
    kind?: 'idea' | 'event_proposal';
    eventDate?: string;
    eventVenue?: string;
    eventCost?: string;
    fileId?: string;
  }) => post<{ id: string; message: string }>('/api/ideas', body),
  adoptIdea: (id: string) => post<{ mine: IdeaDto[]; shared: IdeaDto[] }>(`/api/ideas/${id}/adopt`),
  addIdeaNote: (id: string, body: string) =>
    post<{ mine: IdeaDto[]; shared: IdeaDto[] }>(`/api/ideas/${id}/notes`, { body, kind: 'learning' }),

  library: (q?: string) => get<{ items: LibraryItem[] }>(`/api/library${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  checkin: () => get<{ checkin: CheckinDto | null }>('/api/checkin'),
  submitCheckin: (body: { did: string; wentWell: string; wentBadly: string; wouldHelp: string }) =>
    post<{ checkin: CheckinDto }>('/api/checkin', body),
  submitReview: (body: {
    title: string;
    wentWell: string;
    wouldChange: string;
    thanks: { name: string; reason?: string }[];
  }) => post<{ id: string; message: string }>('/api/reviews', body),

  questions: () => get<{ questions: QuestionDto[] }>('/api/questions'),
  ask: (body: string) => post<{ questions: QuestionDto[]; message: string }>('/api/questions', { body }),
};
